import type { DomainPorts } from './domain';
import { hasNormalizedNameCollision, resolveEffectivePermissionKeys } from './memberManagementRules';
import type { MemberManagementData, Membership, SeasonMembership } from './model';
import type { AttendanceCategory, AttendanceEvent, AttendanceRecord, Schedule } from '../schedule/model';
import { generateAttendanceSnapshot, settleAttendanceRecords } from '../schedule/rules';
import type { ScheduleDetail, ScheduleDraftInput, ScheduleListItem } from './appService';

export interface ScheduleSettlementResult {
  readonly data: MemberManagementData;
  readonly changed: boolean;
}

type AttendanceRecordReadModel = NonNullable<ScheduleDetail['attendance']>['records'][number];

export function findActiveSchedulesInWindow(
  data: MemberManagementData,
  choirId: string,
  seasonId: string,
  from: string,
  to: string
): readonly Schedule[] {
  const window = readUtcCalendarDateWindow(from, to);

  return data.schedules
    .filter(
      (schedule) =>
        schedule.choirId === choirId &&
        schedule.seasonId === seasonId &&
        schedule.status === 'active' &&
        parseIsoMillis(schedule.startsAt, 'schedule.startsAt') >= window.fromInclusive &&
        parseIsoMillis(schedule.startsAt, 'schedule.startsAt') < window.toExclusive
    )
    .sort(compareSchedulesByStart);
}

export function selectHomeScheduleFromSortedList(
  schedules: readonly ScheduleListItem[],
  now: string
): ScheduleListItem | null {
  return selectUpcomingSchedulesFromSortedList(schedules, now, 1)[0] ?? null;
}

export function selectUpcomingSchedulesFromSortedList(
  schedules: readonly ScheduleListItem[],
  now: string,
  limit: number
): readonly ScheduleListItem[] {
  const nowMillis = parseIsoMillis(now, 'now');
  return schedules
    .filter((schedule) => parseIsoMillis(schedule.endsAt, 'schedule.endsAt') >= nowMillis)
    .slice(0, limit);
}

function readUtcCalendarDateWindow(
  from: string,
  to: string
): { readonly fromInclusive: number; readonly toExclusive: number } {
  const fromDate = requireUtcCalendarDate(from, 'from');
  const toDate = requireUtcCalendarDate(to, 'to');

  if (toDate.getTime() < fromDate.getTime()) {
    throw new Error('Schedule list end must not be before start');
  }

  return {
    fromInclusive: fromDate.getTime(),
    toExclusive: toDate.getTime() + 24 * 60 * 60 * 1000
  };
}

function requireUtcCalendarDate(value: string, field: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (!match) {
    throw new Error(`${field} must be a UTC calendar date`);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new Error(`${field} must be a valid UTC calendar date`);
  }

  return date;
}

export function settleSchedulesIfNeeded(
  data: MemberManagementData,
  scheduleIds: readonly string[],
  now: string,
  ports: DomainPorts
): ScheduleSettlementResult {
  return scheduleIds.reduce<ScheduleSettlementResult>(
    (current, scheduleId) => {
      const next = settleScheduleIfNeeded(current.data, scheduleId, now, ports);

      return {
        data: next.data,
        changed: current.changed || next.changed
      };
    },
    { data, changed: false }
  );
}

export function needsScheduleReadSettlement(
  data: MemberManagementData,
  scheduleIds: readonly string[],
  now: string
): boolean {
  return scheduleIds.some((scheduleId) => needsSingleScheduleReadSettlement(data, scheduleId, now));
}

export function toScheduleListItem(
  data: MemberManagementData,
  schedule: Schedule,
  currentMembership: Membership,
  now: string
): ScheduleListItem {
  const attendanceEvent = findAttendanceEventForSchedule(data, schedule.scheduleId);
  const attendanceCategory = attendanceEvent
    ? findAttendanceCategory(data, attendanceEvent.attendanceCategoryId)
    : null;

  return {
    scheduleId: schedule.scheduleId,
    title: schedule.title,
    startsAt: schedule.startsAt,
    endsAt: schedule.endsAt,
    location: schedule.location,
    note: schedule.note,
    attendanceCategoryName: attendanceCategory?.name ?? null,
    attendanceStatus: attendanceEvent ? getAttendanceStatus(schedule, now) : 'not_enabled',
    canManage: canManageSchedule(data, schedule, currentMembership)
  };
}

function canManageSchedule(data: MemberManagementData, schedule: Schedule, currentMembership: Membership): boolean {
  const currentSeasonMembership = findActiveSeasonMembershipForSchedule(data, currentMembership, schedule);

  return resolveEffectivePermissionKeys({
    memberRole: currentMembership.role,
    seasonMembershipId: currentSeasonMembership?.seasonMembershipId,
    choirId: schedule.choirId,
    seasonId: schedule.seasonId,
    permissionGroups: data.permissionGroups,
    permissionGroupPermissions: data.permissionGroupPermissions,
    permissionGroupMembers: data.permissionGroupMembers
  }).includes('schedule_manage');
}

export function toScheduleDetail(
  data: MemberManagementData,
  schedule: Schedule,
  currentMembership: Membership,
  now: string
): ScheduleDetail {
  const listItem = toScheduleListItem(data, schedule, currentMembership, now);
  const attendanceEvent = findAttendanceEventForSchedule(data, schedule.scheduleId);

  if (!attendanceEvent) {
    return {
      ...listItem,
      deletedAt: schedule.deletedAt,
      attendance: null
    };
  }

  const currentSeasonMembership = findActiveSeasonMembershipForSchedule(data, currentMembership, schedule);
  const attendanceRecords = data.attendanceRecords.filter(
    (record) => record.attendanceEventId === attendanceEvent.attendanceEventId
  );
  const snapshotOrder = new Map(
    data.attendanceParticipantSnapshots
      .filter((snapshot) => snapshot.attendanceEventId === attendanceEvent.attendanceEventId)
      .map((snapshot, index) => [snapshot.attendanceParticipantSnapshotId, index])
  );
  const sortedRecords = [...attendanceRecords].sort(
    (left, right) =>
      readSnapshotOrder(snapshotOrder, left.attendanceParticipantSnapshotId) -
      readSnapshotOrder(snapshotOrder, right.attendanceParticipantSnapshotId)
  );
  const currentUserRecord =
    currentSeasonMembership === null
      ? null
      : (attendanceRecords.find((record) => record.seasonMembershipId === currentSeasonMembership.seasonMembershipId) ??
        null);
  const currentUserRecordReadModel = currentUserRecord ? toAttendanceRecordReadModel(data, currentUserRecord) : null;
  const participantMemberGroups = getAttendanceParticipantMemberGroups(data, attendanceEvent);

  return {
    ...listItem,
    deletedAt: schedule.deletedAt,
    attendance: {
      attendanceEventId: attendanceEvent.attendanceEventId,
      category: findAttendanceCategory(data, attendanceEvent.attendanceCategoryId),
      participantMemberGroupIds: participantMemberGroups.map((memberGroup) => memberGroup.memberGroupId),
      participantMemberGroups,
      onTimeReferenceMinutesFromStart: attendanceEvent.onTimeReferenceMinutesFromStart,
      lateReferenceMinutesFromStart: attendanceEvent.lateReferenceMinutesFromStart,
      currentUserRecord: currentUserRecordReadModel,
      records: sortedRecords.map((record) => toAttendanceRecordReadModel(data, record))
    }
  };
}

export function validateScheduleAttendanceDraft(
  data: MemberManagementData,
  choirId: string,
  seasonId: string,
  attendance: NonNullable<ScheduleDraftInput['attendance']>
): NonNullable<ScheduleDraftInput['attendance']> {
  const category = data.attendanceCategories.find(
    (candidate) => candidate.attendanceCategoryId === attendance.attendanceCategoryId
  );

  if (!category) {
    throw new Error('Attendance category not found');
  }

  if (category.choirId !== choirId || category.seasonId !== seasonId) {
    throw new Error('Attendance category is not in the current season');
  }

  if (category.status !== 'enabled') {
    throw new Error('Attendance category is disabled');
  }

  for (const memberGroupId of attendance.participantMemberGroupIds) {
    const memberGroup = data.memberGroups.find((candidate) => candidate.memberGroupId === memberGroupId);

    if (!memberGroup) {
      throw new Error('Attendance scope member group not found');
    }

    if (memberGroup.choirId !== choirId || memberGroup.seasonId !== seasonId) {
      throw new Error('Attendance scope member group is not in the current season');
    }
  }

  return attendance;
}

export function replaceScheduleAttendanceDraft(
  data: MemberManagementData,
  schedule: Schedule,
  existingAttendanceEvent: AttendanceEvent | null,
  attendance: ScheduleDraftInput['attendance'],
  now: string,
  ports: DomainPorts
): MemberManagementData {
  if (attendance === undefined) {
    throw new Error('Attendance draft is required when replacing schedule attendance');
  }

  if (attendance === null) {
    return removeScheduleAttendance(data, existingAttendanceEvent);
  }

  const attendanceEvent: AttendanceEvent = existingAttendanceEvent
    ? {
        ...existingAttendanceEvent,
        attendanceCategoryId: attendance.attendanceCategoryId,
        onTimeReferenceMinutesFromStart: attendance.onTimeReferenceMinutesFromStart,
        lateReferenceMinutesFromStart: attendance.lateReferenceMinutesFromStart,
        updatedAt: now
      }
    : {
        attendanceEventId: ports.newId('attendanceEvent'),
        scheduleId: schedule.scheduleId,
        choirId: schedule.choirId,
        seasonId: schedule.seasonId,
        attendanceCategoryId: attendance.attendanceCategoryId,
        onTimeReferenceMinutesFromStart: attendance.onTimeReferenceMinutesFromStart,
        lateReferenceMinutesFromStart: attendance.lateReferenceMinutesFromStart,
        snapshotGeneratedAt: null,
        settledAt: null,
        createdAt: now,
        updatedAt: now
      };

  return {
    ...data,
    attendanceEvents: existingAttendanceEvent
      ? replaceByAttendanceEventId(data.attendanceEvents, attendanceEvent)
      : [...data.attendanceEvents, attendanceEvent],
    attendanceEventMemberGroups: [
      ...data.attendanceEventMemberGroups.filter(
        (link) => link.attendanceEventId !== attendanceEvent.attendanceEventId
      ),
      ...attendance.participantMemberGroupIds.map((memberGroupId) => ({
        attendanceEventId: attendanceEvent.attendanceEventId,
        memberGroupId
      }))
    ]
  };
}

export function findScheduleForService(
  data: MemberManagementData,
  scheduleId: string,
  currentMembership: Membership,
  currentSeasonId: string
): Schedule {
  const schedule = data.schedules.find((candidate) => candidate.scheduleId === scheduleId);

  if (!schedule) {
    throw new Error('Schedule not found');
  }

  if (schedule.choirId !== currentMembership.choirId) {
    throw new Error('Schedule belongs to a different choir');
  }

  if (schedule.seasonId !== currentSeasonId) {
    throw new Error('Schedule is not in the current season');
  }

  return schedule;
}

export function findAttendanceEventForSchedule(data: MemberManagementData, scheduleId: string): AttendanceEvent | null {
  return data.attendanceEvents.find((event) => event.scheduleId === scheduleId) ?? null;
}

export function findAttendanceCategoryForService(
  data: MemberManagementData,
  attendanceCategoryId: string,
  currentMembership: Membership,
  currentSeasonId: string
): AttendanceCategory {
  const category = findAttendanceCategory(data, attendanceCategoryId);

  if (category.choirId !== currentMembership.choirId) {
    throw new Error('Attendance category belongs to a different choir');
  }

  if (category.seasonId !== currentSeasonId) {
    throw new Error('Attendance category is not in the current season');
  }

  return category;
}

export function findAttendanceCategoriesForSeason(
  data: MemberManagementData,
  choirId: string,
  seasonId: string
): readonly AttendanceCategory[] {
  return data.attendanceCategories
    .filter((category) => category.choirId === choirId && category.seasonId === seasonId)
    .sort((left, right) => left.displayOrder - right.displayOrder);
}

export function hasEnabledAttendanceCategoryNameCollision(
  categories: readonly AttendanceCategory[],
  name: string,
  ignoredAttendanceCategoryId?: string
): boolean {
  return hasNormalizedNameCollision(
    categories.filter((category) => category.status === 'enabled'),
    name,
    'attendanceCategoryId',
    ignoredAttendanceCategoryId
  );
}

export function replaceByScheduleId(schedules: readonly Schedule[], replacement: Schedule): readonly Schedule[] {
  return schedules.map((schedule) => (schedule.scheduleId === replacement.scheduleId ? replacement : schedule));
}

export function replaceByAttendanceCategoryId(
  categories: readonly AttendanceCategory[],
  replacement: AttendanceCategory
): readonly AttendanceCategory[] {
  return categories.map((category) =>
    category.attendanceCategoryId === replacement.attendanceCategoryId ? replacement : category
  );
}

export function replaceByAttendanceRecordId(
  records: readonly AttendanceRecord[],
  replacement: AttendanceRecord
): readonly AttendanceRecord[] {
  return records.map((record) => (record.attendanceRecordId === replacement.attendanceRecordId ? replacement : record));
}

export function compareSchedulesByStart(left: Schedule, right: Schedule): number {
  return (
    parseIsoMillis(left.startsAt, 'schedule.startsAt') - parseIsoMillis(right.startsAt, 'schedule.startsAt') ||
    left.title.localeCompare(right.title) ||
    left.scheduleId.localeCompare(right.scheduleId)
  );
}

export function parseIsoMillis(value: string, label: string): number {
  const time = Date.parse(value);

  if (Number.isNaN(time)) {
    throw new Error(`${label} must be a valid ISO date string`);
  }

  return time;
}

function settleScheduleIfNeeded(
  data: MemberManagementData,
  scheduleId: string,
  now: string,
  ports: DomainPorts
): ScheduleSettlementResult {
  const schedule = data.schedules.find((candidate) => candidate.scheduleId === scheduleId);

  if (!schedule) {
    throw new Error('Schedule not found');
  }

  const attendanceEvent = findAttendanceEventForSchedule(data, schedule.scheduleId);

  if (!attendanceEvent || schedule.status === 'deleted') {
    return { data, changed: false };
  }

  let current = data;
  let currentAttendanceEvent = attendanceEvent;
  let changed = false;

  if (
    currentAttendanceEvent.snapshotGeneratedAt === null &&
    parseIsoMillis(now, 'now') >= parseIsoMillis(schedule.startsAt, 'schedule.startsAt')
  ) {
    const snapshot = generateAttendanceSnapshot({
      attendanceEvent: currentAttendanceEvent,
      now,
      scopedMembers: getScopedMembersForAttendanceEvent(current, currentAttendanceEvent),
      newSnapshotId: () => ports.newId('attendanceParticipantSnapshot'),
      newRecordId: () => ports.newId('attendanceRecord')
    });
    currentAttendanceEvent = snapshot.attendanceEvent;
    current = {
      ...current,
      attendanceEvents: replaceByAttendanceEventId(current.attendanceEvents, currentAttendanceEvent),
      attendanceParticipantSnapshots: [...current.attendanceParticipantSnapshots, ...snapshot.snapshots],
      attendanceRecords: [...current.attendanceRecords, ...snapshot.records]
    };
    changed = true;
  }

  const recordsForEvent = current.attendanceRecords.filter(
    (record) => record.attendanceEventId === currentAttendanceEvent.attendanceEventId
  );
  const settled = settleAttendanceRecords({
    schedule,
    attendanceEvent: currentAttendanceEvent,
    records: recordsForEvent,
    now
  });
  const settlementChanged = settled.attendanceEvent !== currentAttendanceEvent || settled.records !== recordsForEvent;

  if (!settlementChanged) {
    return { data: current, changed };
  }

  return {
    data: {
      ...current,
      attendanceEvents: replaceByAttendanceEventId(current.attendanceEvents, settled.attendanceEvent),
      attendanceRecords: replaceAttendanceRecordsForEvent(
        current.attendanceRecords,
        settled.attendanceEvent.attendanceEventId,
        settled.records
      )
    },
    changed: true
  };
}

function needsSingleScheduleReadSettlement(data: MemberManagementData, scheduleId: string, now: string): boolean {
  const schedule = data.schedules.find((candidate) => candidate.scheduleId === scheduleId);

  if (!schedule) {
    throw new Error('Schedule not found');
  }

  const attendanceEvent = findAttendanceEventForSchedule(data, schedule.scheduleId);

  if (!attendanceEvent || schedule.status === 'deleted') {
    return false;
  }

  const nowTime = parseIsoMillis(now, 'now');

  if (
    attendanceEvent.snapshotGeneratedAt === null &&
    nowTime >= parseIsoMillis(schedule.startsAt, 'schedule.startsAt')
  ) {
    return true;
  }

  return attendanceEvent.settledAt === null && nowTime > parseIsoMillis(schedule.endsAt, 'schedule.endsAt');
}

function getScopedMembersForAttendanceEvent(
  data: MemberManagementData,
  attendanceEvent: AttendanceEvent
): readonly {
  readonly seasonMembershipId: string;
  readonly nickname: string;
  readonly memberGroupNames: readonly string[];
}[] {
  const scopeGroupIds = data.attendanceEventMemberGroups
    .filter((link) => link.attendanceEventId === attendanceEvent.attendanceEventId)
    .map((link) => link.memberGroupId);
  const scopeGroupIdSet = new Set(scopeGroupIds);

  for (const groupId of scopeGroupIds) {
    const group = data.memberGroups.find((candidate) => candidate.memberGroupId === groupId);

    if (!group) {
      throw new Error('Attendance scope member group not found');
    }

    if (group.choirId !== attendanceEvent.choirId || group.seasonId !== attendanceEvent.seasonId) {
      throw new Error('Attendance scope member group is not in the attendance season');
    }
  }

  return data.seasonMemberships
    .filter(
      (seasonMembership) =>
        seasonMembership.choirId === attendanceEvent.choirId &&
        seasonMembership.seasonId === attendanceEvent.seasonId &&
        seasonMembership.status === 'active' &&
        (scopeGroupIdSet.size === 0 ||
          data.seasonMembershipMemberGroups.some(
            (link) =>
              link.seasonMembershipId === seasonMembership.seasonMembershipId && scopeGroupIdSet.has(link.memberGroupId)
          ))
    )
    .map((seasonMembership) => ({
      seasonMembershipId: seasonMembership.seasonMembershipId,
      nickname: seasonMembership.nickname,
      memberGroupNames: getSeasonMembershipMemberGroupNames(data, seasonMembership)
    }));
}

function getAttendanceParticipantMemberGroups(
  data: MemberManagementData,
  attendanceEvent: AttendanceEvent
): readonly {
  readonly memberGroupId: string;
  readonly name: string;
}[] {
  return data.attendanceEventMemberGroups
    .filter((link) => link.attendanceEventId === attendanceEvent.attendanceEventId)
    .map((link) => {
      const memberGroup = data.memberGroups.find((candidate) => candidate.memberGroupId === link.memberGroupId);

      if (!memberGroup) {
        throw new Error('Attendance scope member group not found');
      }

      if (memberGroup.choirId !== attendanceEvent.choirId || memberGroup.seasonId !== attendanceEvent.seasonId) {
        throw new Error('Attendance scope member group is not in the attendance season');
      }

      return {
        memberGroupId: memberGroup.memberGroupId,
        name: memberGroup.name
      };
    });
}

function getSeasonMembershipMemberGroupNames(
  data: MemberManagementData,
  seasonMembership: SeasonMembership
): readonly string[] {
  const memberGroupIds = data.seasonMembershipMemberGroups
    .filter((link) => link.seasonMembershipId === seasonMembership.seasonMembershipId)
    .map((link) => link.memberGroupId);

  return data.memberGroups
    .filter((memberGroup) => memberGroupIds.includes(memberGroup.memberGroupId))
    .sort((left, right) => left.displayOrder - right.displayOrder)
    .map((memberGroup) => memberGroup.name);
}

function readSnapshotOrder(
  snapshotOrder: ReadonlyMap<string, number>,
  attendanceParticipantSnapshotId: string
): number {
  const order = snapshotOrder.get(attendanceParticipantSnapshotId);

  if (order === undefined) {
    throw new Error('Attendance participant snapshot not found');
  }

  return order;
}

function toAttendanceRecordReadModel(data: MemberManagementData, record: AttendanceRecord): AttendanceRecordReadModel {
  const snapshot = data.attendanceParticipantSnapshots.find(
    (candidate) => candidate.attendanceParticipantSnapshotId === record.attendanceParticipantSnapshotId
  );

  if (!snapshot) {
    throw new Error('Attendance participant snapshot not found');
  }

  return {
    attendanceRecordId: record.attendanceRecordId,
    seasonMembershipId: record.seasonMembershipId,
    nickname: snapshot.nicknameSnapshot,
    memberGroupNames: snapshot.memberGroupNamesSnapshot,
    status: record.status,
    selectedAt: record.selectedAt,
    settledAt: record.settledAt
  };
}

function getAttendanceStatus(schedule: Schedule, now: string): ScheduleListItem['attendanceStatus'] {
  const nowTime = parseIsoMillis(now, 'now');

  if (nowTime < parseIsoMillis(schedule.startsAt, 'schedule.startsAt')) {
    return 'not_started';
  }

  if (nowTime <= parseIsoMillis(schedule.endsAt, 'schedule.endsAt')) {
    return 'signing';
  }

  return 'ended';
}

function removeScheduleAttendance(
  data: MemberManagementData,
  attendanceEvent: AttendanceEvent | null
): MemberManagementData {
  if (!attendanceEvent) {
    return data;
  }

  const snapshotIds = new Set(
    data.attendanceParticipantSnapshots
      .filter((snapshot) => snapshot.attendanceEventId === attendanceEvent.attendanceEventId)
      .map((snapshot) => snapshot.attendanceParticipantSnapshotId)
  );

  return {
    ...data,
    attendanceEvents: data.attendanceEvents.filter(
      (candidate) => candidate.attendanceEventId !== attendanceEvent.attendanceEventId
    ),
    attendanceEventMemberGroups: data.attendanceEventMemberGroups.filter(
      (link) => link.attendanceEventId !== attendanceEvent.attendanceEventId
    ),
    attendanceParticipantSnapshots: data.attendanceParticipantSnapshots.filter(
      (snapshot) => snapshot.attendanceEventId !== attendanceEvent.attendanceEventId
    ),
    attendanceRecords: data.attendanceRecords.filter(
      (record) =>
        record.attendanceEventId !== attendanceEvent.attendanceEventId &&
        !snapshotIds.has(record.attendanceParticipantSnapshotId)
    )
  };
}

function findAttendanceCategory(data: MemberManagementData, attendanceCategoryId: string): AttendanceCategory {
  const category = data.attendanceCategories.find(
    (candidate) => candidate.attendanceCategoryId === attendanceCategoryId
  );

  if (!category) {
    throw new Error('Attendance category not found');
  }

  return category;
}

export function findActiveSeasonMembershipForSchedule(
  data: MemberManagementData,
  membership: Membership,
  schedule: Schedule
): SeasonMembership | null {
  if (membership.choirId !== schedule.choirId) {
    throw new Error('Schedule belongs to a different choir');
  }

  return (
    data.seasonMemberships.find(
      (candidate) =>
        candidate.membershipId === membership.membershipId &&
        candidate.choirId === schedule.choirId &&
        candidate.seasonId === schedule.seasonId &&
        candidate.status === 'active'
    ) ?? null
  );
}

function replaceByAttendanceEventId(
  events: readonly AttendanceEvent[],
  replacement: AttendanceEvent
): readonly AttendanceEvent[] {
  return events.map((event) => (event.attendanceEventId === replacement.attendanceEventId ? replacement : event));
}

function replaceAttendanceRecordsForEvent(
  records: readonly AttendanceRecord[],
  attendanceEventId: string,
  replacements: readonly AttendanceRecord[]
): readonly AttendanceRecord[] {
  return [...records.filter((record) => record.attendanceEventId !== attendanceEventId), ...replacements];
}
